import { useLocation, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useProjectContext } from "@/hooks/useProjectContext";
import axios from "axios";
import { getToken } from "@/utils/auth";

interface BreadcrumbItem {
    label: string;
    path: string;
    active?: boolean;
}

// Route mapping configuration
const routeLabels: Record<string, string> = {
    // Main pages
    projects: "Projects",
    products: "Products",
    outlets: "Outlets",
    users: "Users",
    clients: "Clients",
    teams: "Teams",
    sales: "Sales",
    pricing: "Pricing",
    stock: "Stock",
    oos: "OOS Threshold",
    reports: "OOS Threshold",

    // Sub-pages
    "product-groups": "Product Groups",
    "product-types": "Product Types",
    "pack-types": "Pack Types",
    "outlet-groups": "Outlet Groups",
    "outlet-types": "Outlet Types",
    "outlet-categories": "Outlet Categories",
    "employee-categories": "Employee Categories",
    "co-departments": "Departments",
    "regions": "Regions",
    "team-categories": "Team Categories",

    // Actions
    add: "Add",
    edit: "Edit",
    "assign-user": "Assign User",
    "assign-outlet": "Assign Outlet",
    "assign-product": "Assign Product",
};

// Parent route mapping - defines hierarchical relationships
const parentRoutes: Record<string, string> = {
    "product-groups": "products",
    "product-types": "products",
    "pack-types": "products",
    "outlet-groups": "outlets",
    "outlet-types": "outlets",
    "outlet-categories": "outlets",
    "employee-categories": "users",
    "co-departments": "users",
    "regions": "teams",
    "team-categories": "teams",
};

export function useBreadcrumbs() {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
    const [teamName, setTeamName] = useState<string | null>(null);
    const projectContext = useProjectContext();

    const getPathSegments = () => location.pathname.split("/").filter(Boolean);

    // Extract team ID from URL if present (e.g., /teams/{id}/edit)
    const pathSegments = getPathSegments();
    const teamIndex = pathSegments.indexOf("teams");
    const teamId = teamIndex !== -1 && pathSegments[teamIndex + 1] && pathSegments[teamIndex + 1].match(/^[0-9a-f]{8}-[0-9a-f]{4}-/)
        ? pathSegments[teamIndex + 1]
        : null;

    // Fetch team name when teamId is available
    useEffect(() => {
        const fetchTeamName = async () => {
            if (teamId && !teamName) {
                try {
                    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
                    const token = getToken();
                    const res = await axios.get(`${apiUrl}/teams/${teamId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    if (res.data) {
                        setTeamName(res.data?.name || "Team");
                    } else {
                        setTeamName("Team");
                    }
                } catch (error) {
                    console.error("Failed to fetch team:", error);
                    setTeamName("Team");
                }
            } else if (!teamId) {
                setTeamName(null);
            }
        };

        fetchTeamName();
    }, [teamId]);

    useEffect(() => {
        const generateBreadcrumbs = () => {
            const pathSegments = getPathSegments();
            const items: BreadcrumbItem[] = [];
            const companyId = searchParams.get("companyId");

            // Always start with Home (StockLedger dashboard)
            items.push({
                label: "Home",
                path: "/",
                active: false,
            });

            // ✅ Add project name if inside project context
            if (projectContext.isInProject) {
                items.push({
                    label:
                        projectContext.projectName &&
                            projectContext.projectName !== "Projects"
                            ? projectContext.projectName
                            : "Loading...",
                    path: `/projects/${projectContext.projectId}/sales`,
                    active: false,
                });
            }

            // Build remaining segments
            const addedSegments = new Set<string>();
            for (let i = 0; i < pathSegments.length; i++) {
                const segment = pathSegments[i];

                // Handle UUID segments specially - we'll skip them but may add entity names
                if (segment.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/)) {
                    // Check if this is a team ID and we have the team name
                    if (i > 0 && pathSegments[i - 1] === "teams" && teamName) {
                        // Add Teams breadcrumb if not already added
                        if (!addedSegments.has("teams")) {
                            const teamsPath = projectContext.isInProject && projectContext.projectId
                                ? `/teams?projectId=${projectContext.projectId}`
                                : `/teams` + (companyId ? `?companyId=${companyId}` : "");
                            
                            items.push({
                                label: "Teams",
                                path: teamsPath,
                                active: false,
                            });
                            addedSegments.add("teams");
                        }

                        // Add the team name breadcrumb (link to edit page)
                        const teamPath = projectContext.isInProject && projectContext.projectId
                            ? `/teams/${segment}/edit?projectId=${projectContext.projectId}`
                            : `/teams/${segment}/edit` + (companyId ? `?companyId=${companyId}` : "");
                        
                        items.push({
                            label: teamName,
                            path: teamPath,
                            active: false,
                        });
                    }
                    continue;
                }

                // ✅ Skip "projects" when already in project context
                if (projectContext.isInProject && segment === "projects") continue;

                // Handle parent relationships
                let parentRoute = parentRoutes[segment];
                
                // Special handling for "add", "edit", and other actions - determine parent from previous segment
                if ((segment === "add" || segment === "edit" || segment === "assign-user" || segment === "assign-outlet" || segment === "assign-product") && i > 0) {
                    const prevSegment = pathSegments[i - 1];
                    // Map specific routes to their parents
                    if (prevSegment === "products") parentRoute = "products";
                    else if (prevSegment === "users") parentRoute = "users";
                    else if (prevSegment === "outlets") parentRoute = "outlets";
                    else if (prevSegment === "product-groups") parentRoute = "products";
                    else if (prevSegment === "product-types") parentRoute = "products";
                    else if (prevSegment === "pack-types") parentRoute = "products";
                    else if (prevSegment === "outlet-groups") parentRoute = "outlets";
                    else if (prevSegment === "outlet-types") parentRoute = "outlets";
                    else if (prevSegment === "outlet-categories") parentRoute = "outlets";
                    else if (prevSegment === "employee-categories") parentRoute = "users";
                    else if (prevSegment === "co-departments") parentRoute = "users";
                    else if (prevSegment === "teams") parentRoute = "teams";
                }
                
                if (parentRoute && !addedSegments.has(parentRoute)) {
                    const parentLabel =
                        routeLabels[parentRoute] ||
                        parentRoute
                            .split("-")
                            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(" ");
                    
                    // Add projectId to parent path if in project context
                    const parentPath = projectContext.isInProject && projectContext.projectId
                        ? `/${parentRoute}?projectId=${projectContext.projectId}`
                        : `/${parentRoute}` + (companyId ? `?companyId=${companyId}` : "");
                    
                    items.push({
                        label: parentLabel,
                        path: parentPath,
                        active: false,
                    });
                    addedSegments.add(parentRoute);
                }

                // Generate label for this segment
                const label =
                    routeLabels[segment] ||
                    segment
                        .split("-")
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(" ");

                // Construct path up to this segment
                let currentPath = "";
                for (let j = 0; j <= i; j++) {
                    if (!pathSegments[j].match(/^[0-9a-f]{8}-[0-9a-f]{4}-/)) {
                        currentPath += `/${pathSegments[j]}`;
                    }
                }

                // Add projectId to path if in project context
                const finalPath = projectContext.isInProject && projectContext.projectId
                    ? currentPath + `?projectId=${projectContext.projectId}`
                    : currentPath + (companyId ? `?companyId=${companyId}` : "");

                items.push({
                    label,
                    path: finalPath,
                    active: false,
                });
                addedSegments.add(segment);
            }

            // Mark last breadcrumb as active
            if (items.length > 0) items[items.length - 1].active = true;

            setBreadcrumbs(items);
        };

        generateBreadcrumbs();
    }, [
        location.pathname,
        location.search,
        projectContext.projectName,
        projectContext.projectId,
        projectContext.isInProject,
        teamName,
    ]);

    return breadcrumbs;
}
